import classNames from 'classnames';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import styles from './search-bar-destination-group.module.scss';

export interface SearchBarDestinationGroupProps {
  /** The class name to apply */
  className?: string;
  /** Destination group */
  destinationGroup: Content.Components['SearchDestinationNode'][];
  /** Destination group name */
  groupName: string;
  /** An event that is triggered on click */
  onClickDestination?: (
    destination: Content.Components['SearchDestinationNode'],
  ) => void;
}

const SearchBarDestinationGroup = ({
  className,
  destinationGroup,
  groupName,
  onClickDestination,
}: SearchBarDestinationGroupProps) => {
  const highlightText = (
    text: string,
    highlights: { from: number; to: number }[],
  ) => {
    if (!highlights.length) return text;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    highlights.forEach(({ from, to }, index) => {
      if (from > lastIndex) {
        parts.push(text.slice(lastIndex, from));
      }

      parts.push(<strong key={index}>{text.slice(from, to + 1)}</strong>);
      lastIndex = to + 1;
    });

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const getIconName = (nodeType: string): IconName => {
    const iconMap: Record<string, IconName> = {
      container: 'MarkerPin',
      country: 'Flag',
      destination: 'Location',
      destinationRegion: 'MarkerPin',
      hotel: 'Hotel',
    };

    return iconMap[nodeType] || 'MarkerPin';
  };

  return (
    <div className={classNames(styles.component, className)}>
      <div className={styles.groupNameWrapper}>
        <Typography className={styles.groupName} tagStyle="labelMedium">
          {groupName}
        </Typography>
      </div>

      <div className={styles.destinationsWrapper}>
        {destinationGroup.map((destination, destinationIndex) => {
          const ancestors = Array.isArray(destination.ancestors)
            ? destination.ancestors.filter(Boolean)
            : destination.ancestors
              ? [destination.ancestors]
              : [];
          const formattedAncestors = ancestors.length
            ? `, ${ancestors.join(', ')}`
            : '';

          return (
            <button
              key={destinationIndex}
              className={classNames(styles.destination, {
                [styles.isDisabled]:
                  destination.availableDepartures.length === 0,
              })}
              disabled={destination.availableDepartures.length === 0}
              onClick={() => onClickDestination?.(destination)}
              type="button"
            >
              <div className={styles.iconWrapper}>
                <Icon name={getIconName(destination.nodeType)} size={16} />
              </div>

              <Typography tagStyle="bodyMedium">
                {highlightText(destination.name, destination.highlights)}

                {formattedAncestors}
              </Typography>
            </button>
          );
        })}
      </div>
    </div>
  );
};

SearchBarDestinationGroup.displayName = 'SearchBarDestinationGroup';

export default SearchBarDestinationGroup;
